import { z } from 'zod'

import {
  expenseCategorySchema,
  idSchema,
  isoDateSchema,
  isoInstantSchema,
  labelSchema,
  moneyPaisa,
  noteSchema,
  positivePaisaSchema,
  sexSchema,
  sortSchema,
  splitMethodSchema,
  teethAgeSchema,
  versionSchema,
} from '../common'
import { cursorQuerySchema } from '../pagination'

/**
 * A37–A39 · Lots (screens 24 and 25, BR §8, §4.13).
 *
 * **A lot is only how the animals arrived** — six bulls on one truck on one
 * day. It is never a unit of profit: they will be sold one at a time, on
 * different days, at different prices, and a "lot profit" would hide exactly
 * the comparison the trader needs. So `lots` has no money columns; the money
 * lives on the animals and on the cost lines that get split onto them.
 *
 * The split is the part that has to be right. Transport charged per animal is
 * `PER_HEAD`; the dalal's cut is `BY_PRICE`, because he took a share of what
 * each animal cost. Split the dalali per head and a cheap calf carries an
 * expensive bull's commission, and both animals' break-evens are wrong.
 */

/* ── A37 · POST /lots ─────────────────────────────────────────────────────── */

/**
 * One request creates the whole truck: the lot, its N animals, and one derived
 * `expenses` row per animal per cost line — **in a single transaction**. Half a
 * lot is not a state the book should be able to reach.
 *
 * The split is **materialised**, not computed on read: membership and prices
 * are fixed at purchase, so writing the rows once means the split can never
 * drift from what the trader was shown. Editing a line (A39) re-runs it and
 * replaces that line's rows atomically.
 */
export const lotAnimalInput = z.object({
  sex: sexSchema,
  teethAtPurchase: teethAgeSchema,
  pricePaisa: positivePaisaSchema,
  tagNumber: z.string().trim().max(40).optional(),
  nickname: z.string().trim().max(80).optional(),
  breed: z.string().trim().max(40).optional(),
  colour: z.string().trim().max(40).optional(),
})

export const lotCostInput = z.object({
  label: labelSchema,
  category: expenseCategorySchema,
  amountPaisa: positivePaisaSchema,
  /**
   * Defaulted by the server from the category (transport / mandi fee / road
   * feed → per head; dalali → by price) but **always overridable per line**:
   * the trader knows what the man actually charged him.
   *
   * OPTIONAL, and it has to be: the sentence above describes a default the
   * server really does apply (`cost.splitMethod ?? defaultSplitMethod(...)` in
   * `lots.service.ts`), but while this field was required that branch was
   * unreachable from any valid request. Worse, `isOverride` is derived by
   * comparing what arrived against the same default — so a client that did not
   * happen to mirror the server's table flagged every line as a manual
   * override, and the ledger recorded a decision the trader never made.
   */
  splitMethod: splitMethodSchema.optional(),
})

export const createLotRequest = z.object({
  name: labelSchema,
  purchaseDate: isoDateSchema,
  sellerPartyId: idSchema.optional(),
  sellerName: z.string().trim().max(120).optional(),
  sourceMandi: z.string().trim().max(120).optional(),
  notes: noteSchema.optional(),
  /** At least two — one animal on a truck is just an animal (A15). */
  animals: z.array(lotAnimalInput).min(2).max(200),
  costs: z.array(lotCostInput).max(20).default([]),
})

/* ── responses ────────────────────────────────────────────────────────────── */

/** An animal in the lot, with the two figures screen 25 puts side by side. */
export const lotAnimalSchema = z.object({
  animalId: idSchema,
  index: z.number().int().positive(),
  tagNumber: z.string().nullish(),
  nickname: z.string().nullish(),
  sex: sexSchema,
  teethAtPurchase: teethAgeSchema,
  /** What was paid for the animal itself. */
  paidPaisa: moneyPaisa(),
  /** Paid plus this animal's share of every cost line. What it really cost. */
  allInPaisa: moneyPaisa(),
})

/**
 * A cost line and **the derived split it produced** — the "split explanation"
 * A38 promises. `perAnimal` is the actual paisa written to each animal, so the
 * screen shows the real allocation rather than re-dividing the total and
 * disagreeing with the ledger by a rupee of rounding.
 */
export const lotCostSchema = z.object({
  id: idSchema,
  label: labelSchema,
  category: expenseCategorySchema,
  amountPaisa: moneyPaisa(),
  splitMethod: splitMethodSchema,
  /** True when the trader overrode the category's default method. */
  isOverride: z.boolean(),
  perAnimal: z.array(
    z.object({
      animalId: idSchema,
      amountPaisa: moneyPaisa(),
      expenseId: idSchema.nullish(),
    }),
  ),
})

export const lotSummarySchema = z.object({
  id: idSchema,
  name: labelSchema,
  purchaseDate: isoDateSchema,
  daysHeld: z.number().int().nonnegative(),
  animalCount: z.number().int().nonnegative(),
  soldCount: z.number().int().nonnegative(),
  sellerPartyId: idSchema.nullish(),
  sellerName: z.string().nullish(),
  sourceMandi: z.string().nullish(),
  totalPaidPaisa: moneyPaisa(),
  version: versionSchema,
})

export const lotDetailResponse = lotSummarySchema.extend({
  asOf: isoDateSchema,
  notes: noteSchema.nullish(),
  animals: z.array(lotAnimalSchema),
  costs: z.array(lotCostSchema),
  /** Sum of the animals' own prices. */
  animalsTotalPaisa: moneyPaisa(),
  /** Sum of the cost lines. */
  costsTotalPaisa: moneyPaisa(),
  /** Animals + costs: the cheque he actually wrote for the truck. */
  totalPaidPaisa: moneyPaisa(),
  /** All-in per head, for "was the truck a good buy". */
  averagePaisa: moneyPaisa(),
  /** Cost basis of the lot's animals today — paid, plus everything since. */
  worthTodayPaisa: moneyPaisa(),
  createdAt: isoInstantSchema,
})

/* ── A38 · GET /lots ──────────────────────────────────────────────────────── */

export const LOT_SORTS = ['-purchaseDate', 'purchaseDate', 'name'] as const

export const lotListQuery = cursorQuerySchema.extend({
  sort: sortSchema(LOT_SORTS, '-purchaseDate'),
  /** Lots that still have animals in stock. */
  hasHeldAnimals: z.coerce.boolean().optional(),
})

export const lotListResponse = z.array(lotSummarySchema)

/* ── A39 · PATCH /lots/:id/costs/:costId ──────────────────────────────────── */

/**
 * Changing an amount or a split method re-derives that line's expense rows —
 * deleting the old ones and writing the new ones in one transaction, so no
 * animal is ever carrying half of two different splits.
 */
export const patchLotCostRequest = z
  .object({
    label: labelSchema.optional(),
    category: expenseCategorySchema.optional(),
    amountPaisa: positivePaisaSchema.optional(),
    splitMethod: splitMethodSchema.optional(),
  })
  .refine((v) => Object.keys(v).length > 0, 'Nothing to change.')

export type CreateLotRequest = z.input<typeof createLotRequest>
export type LotAnimalInput = z.input<typeof lotAnimalInput>
export type LotCostInput = z.input<typeof lotCostInput>
export type LotSummary = z.infer<typeof lotSummarySchema>
export type LotDetail = z.infer<typeof lotDetailResponse>
export type LotCost = z.infer<typeof lotCostSchema>
export type LotListQuery = z.input<typeof lotListQuery>
export type PatchLotCostRequest = z.input<typeof patchLotCostRequest>
